import React from 'react';
import { useTranslations } from 'next-intl';
import {
  ICustomerAddress,
  PreferredShippingMethod,
  ShippingMethodTypes,
} from '@monofront/types';
import { useLocalOrRemoteCustomer } from '@hooks/useLocalOrRemoteCustomer';
import {
  machineSelectedAddressSelector,
  machineSelectedShippingMethodSelector,
} from '@monofront/business/stateMachines/deliveryBar/selectors';
import { DeliveryBarMachineContext } from '@monofront/business/contexts';

type PickupShippingMethodType = Extract<
  PreferredShippingMethod,
  { type: ShippingMethodTypes.PICKUP }
>;

const CourierAddress = ({ address }: { address: ICustomerAddress }) => {
  const tDeliveryBarAddressForm = useTranslations('DeliveryBar.AddressForm');
  const tDeliveryBar = useTranslations('DeliveryBar.ShippingMethodSelection');
  const { street, postalCode, city } = address;

  if (!street || !postalCode || !city) return null;

  return (
    <p className="tw-lf-body-regular-sm">
      {tDeliveryBar('deliveryTo')}
      <span className="tw-lf-body-bold-sm">
        {tDeliveryBarAddressForm('completeAddress', {
          street,
          postcode: postalCode,
          city,
        })}
      </span>
    </p>
  );
};

const PickupPoint = ({
  pickupPoint,
}: {
  pickupPoint: PickupShippingMethodType;
}) => {
  if (!pickupPoint?.pickupPointCity) {
    return null;
  }

  return (
    <div className="tw-space-y-1">
      <p className="tw-lf-body-bold-sm tw-capitalize">
        {pickupPoint.pickupPointLabel?.toLowerCase()}
      </p>
      <p className="tw-lf-body-regular-sm tw-capitalize">
        {pickupPoint.pickupPointStreet?.toLowerCase()}
      </p>
      <p className="tw-lf-body-regular-sm tw-capitalize">
        {`${pickupPoint.pickupPointZipCode?.toLowerCase() ?? ''} ${
          pickupPoint.pickupPointCity?.toLowerCase() ?? ''
        }`.trim()}
      </p>
    </div>
  );
};

export const DeliveryAddressDisplay = () => {
  const { resolvedCustomer: customer } = useLocalOrRemoteCustomer();
  const selectedAddress = DeliveryBarMachineContext.useSelector(
    machineSelectedAddressSelector,
  );
  const selectedShippingMethod = DeliveryBarMachineContext.useSelector(
    machineSelectedShippingMethodSelector,
  );

  const preferredShippingMethod =
    customer?.preferredShippingMethod ?? selectedShippingMethod;

  if (!preferredShippingMethod || !selectedAddress) return null;

  switch (preferredShippingMethod.type) {
    case ShippingMethodTypes.COURIER:
    case ShippingMethodTypes.HOME:
      if (!customer?.address) return null;

      return <CourierAddress address={selectedAddress} />;

    case ShippingMethodTypes.PICKUP:
      return (
        <PickupPoint
          pickupPoint={
            preferredShippingMethod as unknown as PickupShippingMethodType
          }
        />
      );

    default:
      return null;
  }
};
